// Libraries:
import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import PropTypes from "prop-types";
import format from "date-fns/format";

// Components:
import PageMeta from "core/PageMeta";
import MoreFromThis from "core/MoreFromThis";
import AboutThisImage from "core/AboutThisImage";
import PhotoDetailSidebar from "photos/PhotoDetailSidebar";
import AssetDownload, { HeroAsset } from "core/AssetDownload";
import HeroImage from "./HeroImage";

// assetDownloadConfig
import { assetDownloadConfig } from "./assetDownloadConfig";

// Paths:
import {
    BROWSE_BY_SHOOT_ID,
    PHOTO_DETAIL,
    BROWSE_BY_CAMPAIGN,
    BROWSE_PHOTOS_BY_TAG
} from "data/links";

// components

// Helpers
import { MORE_PHOTOS_FROM_SHOOT_BY_PHOTO_ID } from "photos/PhotoSearchResults/queries";
import { getMoreFromThisData } from "core/MoreFromThis/helpers";
import { getTagsData } from "core/TagsShowMoreLess/helpers";
import { getCampaignsData } from "core/AboutThisImage/helpers";
import { capitalizeFirstChar } from "frontend/components/helpers";
import { TRACK_ITEM_PHOTO, trackViewItem } from "core/trackEvent";

const PhotoDetail = ({ photo }) => {
    const [moreFromThisShootResponse, setMoreFromThisShootResponse] = useState(
        null
    );
    const [photoItems, setPhotoItems] = useState([]);
    const [photoDetail, setPhotoDetail] = useState(null);
    const [photoTags, setPhotoTags] = useState([]);
    const [campaignItems, setCampaignItems] = useState([]);

    const setData = () => {
        setMoreFromThisShootResponse(data.morePhotosFromThisShoot);
        setPhotoDetail(data.photoDetail);
    };

    const [loadMoreFromShoot, { called, loading, data }] = useLazyQuery(
        MORE_PHOTOS_FROM_SHOOT_BY_PHOTO_ID,
        {
            fetchPolicy: "cache-and-network",
            onCompleted: setData
        }
    );

    useEffect(() => {
        if (photo) {
            trackViewItem({
                itemId: photo.id,
                itemName: photo.title,
                itemType: TRACK_ITEM_PHOTO
            });
        }
    }, [!photo]);

    /****************************
     *
     *
     * More From This Shoot:
     *
     * **************************/

    if (photo && photo.id && !called) {
        loadMoreFromShoot({
            variables: {
                photoId: photo.id,
                limit: 50
            }
        });
    }

    const moreFromThisShootHeading = "More from this shoot";

    // // create view all link for "More From This Shoot":
    const firstPhoto =
        moreFromThisShootResponse &&
        moreFromThisShootResponse.items[0] &&
        moreFromThisShootResponse.items[0].photoShoot;
    const shootSlugWithId = (firstPhoto && firstPhoto.slugWithId) || "";
    const moreFromThisShootViewAllLink = BROWSE_BY_SHOOT_ID.replace(
        ":shootInfo",
        `${shootSlugWithId}`
    );

    // create each "More From This Shoot" image data-testid:
    const moreFromThisShootDataTestId = "more-from-shoot-photo";

    /** */

    /****************************
     *
     *
     * Upload date & Tags:
     *
     * **************************/
    const hasCreatedAt = photo && photo.createdAt;

    let formattedCreatedAt = "";

    if (hasCreatedAt) {
        formattedCreatedAt = format(new Date(photo.createdAt), "MMMM DD, YYYY");
    }

    const hasPhotoTags = photo && photo.tags;

    let imageTags = [];

    if (hasPhotoTags) {
        imageTags = photo.tags; // setting photoTags to photo.tags was causing an infinite re-render loop
    }
    /** */

    // create reusable const that tells "More from this shoot", "tags", and "campaigns" helpers what type of detail page this is:
    const pageContentType = "photos";

    useEffect(() => {
        // reformat data for More From This Shoot for component:
        if (moreFromThisShootResponse) {
            setPhotoItems(
                getMoreFromThisData({
                    items: moreFromThisShootResponse.items,
                    detailUrlPath: PHOTO_DETAIL,
                    detailUrlPathReplace: ":photoSlugWithId",
                    type: pageContentType
                })
            );
        }

        // reformat data for Tags for component:
        if (hasPhotoTags) {
            setPhotoTags(
                getTagsData({
                    tags: imageTags,
                    tagPath: BROWSE_PHOTOS_BY_TAG,
                    tagPathReplace: ":term",
                    type: pageContentType
                })
            );
        }

        // reformat data for Campaigns for component:
        if (photoDetail) {
            setCampaignItems(
                getCampaignsData({
                    campaigns: photoDetail.campaigns,
                    campaignPath: BROWSE_BY_CAMPAIGN,
                    campaignPathReplace: ":campaignDetail",
                    type: pageContentType
                })
            );
        }
    }, [moreFromThisShootResponse, photoDetail]);

    let metaTags;
    let metaDescription = "";

    const marketingLine =
        "Noun Project’s HD pictures are curated for quality & diversity.";

    if (photo) {
        const hasPhotoDescription = !!photo.description;

        metaTags = Array.isArray(photo.tags)
            ? photo.tags.map(tag => tag.name)
            : [];

        if (!hasPhotoDescription && !metaTags.length) {
            metaDescription = `Free image - ${marketingLine}`;
        } else {
            let firstLine = "";

            if (hasPhotoDescription) {
                firstLine = photo.description;
            } else {
                let firstTag = metaTags[0]
                    ? capitalizeFirstChar({
                          str: metaTags[0]
                      })
                    : "";
                let secondTag = metaTags[1]
                    ? capitalizeFirstChar({
                          str: metaTags[1]
                      })
                    : "";
                let thirdTag = metaTags[2]
                    ? capitalizeFirstChar({
                          str: metaTags[2]
                      })
                    : "";

                if (metaTags.length === 1) {
                    firstLine = `${firstTag} photo`;
                }

                if (metaTags.length === 2) {
                    firstLine = `${firstTag} & ${secondTag} photo`;
                }

                if (metaTags.length >= 3) {
                    firstLine = `${firstTag}, ${secondTag} & ${thirdTag} photo`;
                }
            }

            metaDescription = `Free image - ${firstLine}. ${marketingLine}`;
        }
    }

    return (
        <>
            {photo && (
                <PageMeta
                    title={`${photo.title} - Free Photo (${photo.id}) - Noun Project`}
                    description={metaDescription}
                    indexContent="index,follow,max-image-preview:large"
                    useSocialMediaTags
                    previewImg={photo.assets.preview}
                    twitterDescription={`Photo by ${photo.creator.name} from @NounProject`}
                    twitterCardContent="summary_large_image"
                    structuredData={{
                        "@context": "https://schema.org",
                        "@type": "ImageObject",
                        additionalType: "photo",
                        name: `${photo.title} ${photo.id}`,
                        author: photo.creator.name,
                        contentUrl: photo.assets.preview,
                        width: "200",
                        height: "200",
                        description: metaDescription,
                        keywords: metaTags,
                        publisher: "The Noun Project",
                        acquireLicensePage:
                            window.location.origin +
                            PHOTO_DETAIL.replace(
                                ":photoSlugWithId",
                                photo.slugWithId
                            ),
                        isAccessibleForFree: true,
                        license: "https://thenounproject.com/pricing/#photos"
                    }}
                />
            )}

            <AssetDownload
                asset={photo}
                assetType="photo"
                assetDownloadConfig={assetDownloadConfig}
            >
                <HeroAsset>
                    <HeroImage photo={photo} />
                </HeroAsset>
                <PhotoDetailSidebar photo={photo} />
            </AssetDownload>

            <MoreFromThis
                items={photoItems}
                heading={moreFromThisShootHeading}
                viewAllLink={moreFromThisShootViewAllLink}
                eachDataTestId={moreFromThisShootDataTestId}
            />

            <AboutThisImage
                sectionHeading="About This Photo"
                sectionDataTestId="photo-detail-about"
                image={photo}
                tags={photoTags}
                campaignLinkPrefix="This photo is included in:"
                campaigns={campaignItems}
                createdAt={formattedCreatedAt}
            />
        </>
    );
};

PhotoDetail.propTypes = {
    photoId: PropTypes.string.isRequired,
    photo: PropTypes.object
};

export default PhotoDetail;
